import React, { useContext, useState, useEffect } from 'react';
import {
  InlineLoading,
  Button,
  FileUploaderButton,
  Checkbox
} from 'carbon-components-react';
import { useSelector } from 'react-redux';
import { currentUser } from 'redux/session/selectors';
import { Close16 } from '@carbon/icons-react';
import MapSettings from 'components/Map/settings';
import { zoomToFeature } from 'components/Map/helper';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import {
  setMultipleShapes,
  uploadEnquiryShapeStart,
  uploadEnquiryShapeFailure,
  setUseUploadedShapeUserReference,
  getAssetOwnerCountStart,
  getAssetOwnerCountFailure
} from '../../localStore/actions';
import { addGraphics } from '../map/helper';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import { useEmptyCallback } from 'utilities/hooks.util';

const availableGeometryType = ['linestring', 'polygon'];

const convertGeometryType = (type: string) => {
  return type === 'linestring' ? 'polyline' : 'polygon';
};

const EnquiryUploadPanel: React.FC<Props> = ({ onClose }) => {
  const user = useSelector(currentUser);
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const {
    uploadError,
    uploadedShapes,
    uploading,
    uploadedShapesUserReferences,
    useUploadedShapeUserReference
  } = localStore;

  const [useShapeUserReference, setUseShapeUserReference] = useState<boolean>(
    useUploadedShapeUserReference || false
  );

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const handleCheckboxChange = (checked: boolean) => {
    dispatchSetUseUploadedUserRef(checked);
  };

  const onShapeFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const target = evt.target;
    if (target.files) {
      setIsUploading(true);
      localDispatch(uploadEnquiryShapeStart(target.files[0]));
    }
  };

  const dispatchSetMultipleShapes = useEmptyCallback((shapes: any[]) => {
    localDispatch(setMultipleShapes(shapes));
  });

  const dispatchUploadError = useEmptyCallback((error: string) => {
    localDispatch(uploadEnquiryShapeFailure(error));
  });

  const dispatchSetUseUploadedUserRef = useEmptyCallback((checked: boolean) => {
    localDispatch(setUseUploadedShapeUserReference(checked));
  });

  const dispatchGetAssetOwnerCount = useEmptyCallback(
    (shapes: any[], orgId?: number) => {
      if (shapes.length > 0) {
        localDispatch(
          getAssetOwnerCountStart({
            shapes: shapes,
            orgId
          })
        );
      } else {
        //reset assetowner count
        localDispatch(getAssetOwnerCountFailure());
      }
    }
  );

  useEffect(() => {
    if (useUploadedShapeUserReference !== undefined) {
      setUseShapeUserReference(useUploadedShapeUserReference);
    }
  }, [useUploadedShapeUserReference]);

  useEffect(() => {
    if (uploadedShapes.length > 0) {
      const lastShape: any = uploadedShapes[uploadedShapes.length - 1];
      const shapeTosend = [
        {
          type: lastShape.type,
          coordinates: [lastShape?.coordinates[0]]
        }
      ];
      dispatchGetAssetOwnerCount(shapeTosend, 0);
      const { loadedMapModules, loadedMap } = window;
      const { Graphic, WebMercatorUtils } = loadedMapModules;
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      try {
        const userRefs: Array<any> = [];
        const graphics = uploadedShapes.map((uploadedShape, idx) => {
          if (useShapeUserReference) {
            userRefs.push(uploadedShapesUserReferences[idx]);
          }
          if (
            !availableGeometryType.includes(
              uploadedShape?.type?.toLocaleLowerCase()
            )
          ) {
            throw new Error('Invalid geometry type');
          }

          const geometryType = convertGeometryType(
            uploadedShape.type.toLocaleLowerCase()
          );
          const geometry = geojsonToArcGIS(uploadedShape);
          const webMercatorGeometry =
            WebMercatorUtils.geographicToWebMercator(geometry);

          const graphic = new Graphic({
            geometry: {
              ...webMercatorGeometry,
              type: geometryType
            },
            symbol: MapSettings.polygonSymbol
          });
          return graphic;
        });
        const extent = graphics.reduce((extent, graphic) => {
          if (extent) {
            return extent.union(graphic.geometry.extent);
          }
          return graphic.geometry.extent;
        }, undefined);
        const results = addGraphics({
          graphic: undefined,
          drawingLayer,
          graphics,
          tool: 'polygon',
          userReference: userRefs
        });
        dispatchSetMultipleShapes(results);
        zoomToFeature(extent, 0, true);
      } catch (e) {
        drawingLayer.removeAll();
        e instanceof Error && dispatchUploadError(e.message);
      }
    }
  }, [
    uploadedShapes,
    uploadedShapesUserReferences,
    useShapeUserReference,
    dispatchSetMultipleShapes,
    dispatchUploadError,
    dispatchGetAssetOwnerCount
  ]);

  return (
    <div className="enquiry-upload-panel">
      <div className="enquiry-upload-panel-header">
        <Button size="small" kind="ghost" onClick={() => onClose()}>
          <Close16 />
        </Button>
      </div>
      <div className="enquiry-upload-panel-body">
        <div className="enquiry-upload-panel-form">
          <div className="enquiry-upload-panel-title">Upload shape</div>
          <div className="enquiry-upload-panel-button">
            {uploading ? (
              <Button kind="tertiary" disabled={true}>
                <InlineLoading description="Processing ..." />
              </Button>
            ) : (
              <FileUploaderButton
                buttonKind="tertiary"
                labelText="Browse files ..."
                disableLabelChanges={true}
                onChange={onShapeFileChange}
                accept={['.zip']}
              />
            )}
          </div>
          {uploadError && isUploading ? (
            <div className="enquiry-upload-panel-error">{uploadError}</div>
          ) : (
            <div className="enquiry-upload-panel-instruction">
              {uploading
                ? 'Depending on the complexity of the data, processing may take several minutes'
                : 'Please browse compatible GIS single or multipart data files to upload to the map'}
            </div>
          )}
        </div>
        {!user?.appFeatures?.includes('DISABLED_LARGE_SCALE_ENQUIRIES') && (
          <div className="enquiry-upload-panel-user-reference">
            <div className="enquiry-upload-panel-user-reference-checkbox">
              <Checkbox
                id="userReferenceCheckbox"
                labelText="Use existing shape names as the User reference"
                defaultChecked={useShapeUserReference}
                onChange={(checked: boolean) => handleCheckboxChange(checked)}
              />
            </div>
            <div className="enquiry-upload-panel-user-reference-description">
              Ensure that your shape file template uses &quot;USER_REF&quot; as
              the column name for SmarterWX Locate to extract the user reference
              from the shape file.
            </div>
            <div className="enquiry-upload-panel-user-reference-description">
              You can use any existing shape names within the uploaded file as
              the optional <span>User reference</span> for enquiries.
            </div>
            <div className="enquiry-upload-panel-user-reference-description">
              For any unnamed shape in the file, the approximate address is used
              as the <span>User reference</span> for that area.
            </div>
            <div className="enquiry-upload-panel-user-reference-description">
              If you uncheck this option, each shape&apos;s approximate address
              is used as the <span>User reference</span>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  onClose: Function;
};

export default React.memo(EnquiryUploadPanel);
